import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Container,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  HStack,
  Text,
  Link,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
  IconButton,
  FormErrorMessage,
  FormHelperText,
  Select,
  useToast,
} from '@chakra-ui/react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { signUp } from '../services/apiService'
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { format } from 'date-fns'
import { ko } from 'date-fns/locale'

// react-datepicker에 한국어 locale 등록
registerLocale('ko', ko)

const SignUp: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth, setAuth] = useRecoilState(authState)
  const toast = useToast()
  
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const validationSchema = Yup.object({
    account: Yup.string()
      .min(4, t('아이디는 최소 4자 이상이어야 합니다'))
      .max(20, t('아이디는 최대 20자까지 가능합니다'))
      .required(t('아이디를 입력하세요')),
    password: Yup.string()
      .min(6, t('비밀번호는 최소 6자 이상이어야 합니다'))
      .required(t('비밀번호를 입력하세요')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], t('비밀번호가 일치하지 않습니다'))
      .required(t('비밀번호 확인을 입력하세요')),
    username: Yup.string()
      .min(2, t('사용자명은 최소 2자 이상이어야 합니다'))
      .max(20, t('사용자명은 최대 20자까지 가능합니다'))
      .required(t('사용자명을 입력하세요')),
    birthDate: Yup.string()
      .matches(/^\d{4}-\d{2}-\d{2}$/, t('생년월일은 YYYY-MM-DD 형식이어야 합니다'))
      .required(t('생년월일을 선택하세요')),
    gender: Yup.string()
      .oneOf(['M', 'F'], t('성별을 선택해주세요'))
      .required(t('성별을 선택해주세요')),
    phone: Yup.string()
      .matches(/^01[0-9]\d{3,4}\d{4}$/, t('올바른 핸드폰번호 형식이 아닙니다 (01012341234)'))
      .required(t('핸드폰번호를 입력하세요')),
    email: Yup.string()
      .email(t('올바른 이메일 형식이 아닙니다'))
      .required(t('이메일을 입력하세요')),
    officeId: Yup.number()
      .typeError(t('소속을 선택해주세요'))
      .required(t('소속을 선택해주세요')),
  })

  const offices = [
    { id: 1, name: '본사' },
    { id: 2, name: '광주/나주/목포' },
    { id: 3, name: '전주1' },
    { id: 4, name: '부산' },
    { id: 5, name: '세종' },
    { id: 6, name: '대구' },
    { id: 7, name: '구미' },
    { id: 8, name: '청주1' },
    { id: 9, name: '청주2' },
    { id: 10, name: '인천' },
    { id: 11, name: '안산' },
    { id: 12, name: '전주2' },
    { id: 13, name: '익산' },
  ]

  const formik = useFormik({
    initialValues: {
      account: '',
      password: '',
      confirmPassword: '',
      username: '',
      birthDate: '',
      gender: '',
      phone: '',
      email: '',
      officeId: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setError('')
      setIsLoading(true)

      try {
        const officeParam = values.officeId ? Number(values.officeId) : undefined

        const { data: res } = await signUp(
          values.account,
          values.password,
          values.username,
          values.birthDate,
          values.gender,
          values.phone,
           values.email,
           officeParam
        )
        if (res.statusCode === 200) {
          toast({
            title: t('회원가입 완료'),
            description: t('관리자 승인 후 로그인 가능합니다. 승인까지 영업일 기준 1~2일이 소요될 수 있습니다.'),
            status: 'success',
            duration: 4000,
            isClosable: true,
          })
          navigate('/signIn')
        } else {
          setError(res.message || t('회원가입에 실패했습니다.'))
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || t('회원가입에 실패했습니다.')
        setError(errorMessage)
        
        // 400 오류일 때 토스트 표시
        if (error.response?.status === 400) {
          toast({
            title: t('회원가입 실패'),
            description: errorMessage,
            status: 'error',
            duration: 3000,
            isClosable: true,
          })
        }
      } finally {
        setIsLoading(false)
      }
    },
  })

  return (
    <DefaultLayout>
      <Container maxW="md" py={20}>
        <VStack spacing={8}>
          <Heading size="xl" color="brand.600">
            {t('회원가입')}
          </Heading>

          <Box w="100%" bg="white" p={8} borderRadius="lg" boxShadow="md">
            <Alert status="info" mb={4} borderRadius="md">
              <AlertIcon />
              {t('회원가입 후 관리자 승인 완료 시 로그인하실 수 있습니다.')}
            </Alert>

            {error && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <form onSubmit={formik.handleSubmit}>
              <VStack spacing={6}>
                <FormControl isRequired isInvalid={formik.touched.account && !!formik.errors.account}>
                  <FormLabel>{t('아이디')}</FormLabel>
                  <Input
                    type="text"
                    name="account"
                    value={formik.values.account}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t('아이디를 입력하세요 (4-20자)')}
                    size="lg"
                  />
                  <FormErrorMessage>{formik.errors.account}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={formik.touched.password && !!formik.errors.password}>
                  <FormLabel>{t('비밀번호')}</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder={t('비밀번호를 입력하세요 (6자 이상)')}
                      size="lg"
                    />
                    <InputRightElement h="full">
                      <IconButton
                        aria-label={showPassword ? t('비밀번호 숨기기') : t('비밀번호 보기')}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        variant="ghost"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{formik.errors.password}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}>
                  <FormLabel>{t('비밀번호 확인')}</FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder={t('비밀번호를 다시 입력하세요')}
                      size="lg"
                    />
                    <InputRightElement h="full">
                      <IconButton
                        aria-label={showConfirmPassword ? t('비밀번호 숨기기') : t('비밀번호 보기')}
                        icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                        variant="ghost"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{formik.errors.confirmPassword}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={formik.touched.username && !!formik.errors.username}>
                  <FormLabel>{t('사용자명')}</FormLabel>
                  <Input
                    type="text"
                    name="username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t('사용자명을 입력하세요 (2-20자)')}
                    size="lg"
                  />
                  <FormErrorMessage>{formik.errors.username}</FormErrorMessage>
                  <FormHelperText color="red.500">
                    {t('본명이 아닐 경우 가입 승인이 거절될 수 있습니다.')}
                  </FormHelperText>
                </FormControl>

                <FormControl isRequired isInvalid={formik.touched.birthDate && !!formik.errors.birthDate}>
                  <FormLabel>{t('생년월일')}</FormLabel>
                  <Box
                    sx={{
                      '.react-datepicker-wrapper': {
                        width: '100%'
                      },
                      '.react-datepicker__input-container': {
                        width: '100%'
                      },
                      '.react-datepicker__input-container input': {
                        width: '100%',
                        height: '48px',
                        padding: '0 16px',
                        fontSize: '16px',
                        border: formik.touched.birthDate && formik.errors.birthDate
                          ? '2px solid #E53E3E'
                          : '1px solid #E2E8F0',
                        borderRadius: '6px',
                        outline: 'none',
                        _focus: {
                          borderColor: '#A74230',
                          boxShadow: '0 0 0 1px #A74230'
                        }
                      }
                    }}
                  >
                    <DatePicker
                      selected={formik.values.birthDate ? new Date(formik.values.birthDate) : null}
                      onChange={(date: Date | null) => {
                        if (date) {
                          formik.setFieldValue('birthDate', format(date, 'yyyy-MM-dd'))
                        } else {
                          formik.setFieldValue('birthDate', '')
                        }
                      }}
                      onBlur={formik.handleBlur}
                      dateFormat="yyyy년 MM월 dd일"
                      placeholderText={t('생년월일을 선택하세요')}
                      maxDate={new Date()}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      isClearable
                      className="chakra-input"
                      locale="ko"
                    />
                  </Box>
                  <FormErrorMessage>{formik.errors.birthDate}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={formik.touched.gender && !!formik.errors.gender}>
                  <FormLabel>{t('성별')}</FormLabel>
                  <HStack spacing={4}>
                    <Button
                      type="button"
                      variant={formik.values.gender === 'M' ? 'solid' : 'outline'}
                      colorScheme={formik.values.gender === 'M' ? 'brand' : 'gray'}
                      onClick={() => formik.setFieldValue('gender', 'M')}
                      flex={1}
                    >
                      {t('남성')}
                    </Button>
                    <Button
                      type="button"
                      variant={formik.values.gender === 'F' ? 'solid' : 'outline'}
                      colorScheme={formik.values.gender === 'F' ? 'brand' : 'gray'}
                      onClick={() => formik.setFieldValue('gender', 'F')}
                      flex={1}
                    >
                      {t('여성')}
                    </Button>
                  </HStack>
                  <FormErrorMessage>{formik.errors.gender}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={formik.touched.phone && !!formik.errors.phone}>
                  <FormLabel>{t('핸드폰번호')}</FormLabel>
                  <Input
                    type="tel"
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t('01012341234')}
                    size="lg"
                  />
                  <FormErrorMessage>{formik.errors.phone}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={formik.touched.email && !!formik.errors.email}>
                  <FormLabel>{t('이메일')}</FormLabel>
                  <Input
                    type="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t('example@email.com')}
                    size="lg"
                  />
                  <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={formik.touched.officeId && !!formik.errors.officeId}>
                  <FormLabel>{t('소속')}</FormLabel>
                  <Select
                    placeholder={t('소속을 선택하세요')}
                    name="officeId"
                    value={formik.values.officeId || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      formik.setFieldValue('officeId', value ? Number(value) : '')
                    }}
                    onBlur={formik.handleBlur}
                    size="lg"
                  >
                    {offices.map((office) => (
                      <option key={office.id} value={office.id}>
                        {office.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{formik.errors.officeId}</FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  w="100%"
                  isLoading={isLoading}
                  loadingText={t('가입 중...')}
                >
                  {t('회원가입')}
                </Button>

                <Text>
                  {t('이미 계정이 있으신가요?')}{' '}
                  <Link as={RouterLink} to="/signIn" color="brand.500">
                    {t('로그인')}
                  </Link>
                </Text>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default SignUp
